import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  MonoTypeOperatorFunction,
  Observable,
  catchError,
  finalize,
  forkJoin,
  map,
  of,
  retry,
  shareReplay,
  switchMap,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { BaseService } from '../admin-core/services';
import { RequestParamModel } from '../admin-core/models';
import { Constants } from '../admin-core/constants';

/** What one card needs — never the SKU, which is not shown on the card. */
export interface ProductCard {
  name: string;
  /** Router commands for the console's product detail page (`/products/:id/:type`). */
  link: (string | number)[];
  imageUrl: string;
}

/**
 * When a card whose lookup FAILED asks again, after the service's own quick retries are
 * spent. A failed lookup is never cached, but without this a blank entry beside a product
 * name stayed blank until a reload. Every card re-asks on this schedule; cards asking at the
 * same moment share one request.
 */
export const PRODUCT_CARD_RETRY_LATER_MS = [5_000, 15_000, 45_000] as const;

/**
 * Resolves the SKUs in a Pebbl `product_cards` frame to cards.
 *
 * The frame carries SKUs only and the AI service has no product endpoint, so name,
 * imagery and link come from the product API — the same `GET product` +
 * `filters[sku][in]` + `formatHeroImage` lookup the storefront's chat uses.
 *
 * Results are cached per SKU for the life of the app: the strip prefetches as soon as
 * the frame lands (before any reply text), and re-renders per streamed token, switching
 * chats or reopening the widget never fetch again. A failed request is NOT cached, so a
 * network blip does not hide those cards for the rest of the session: a transient failure
 * (no response, 408, 429, 5xx) is retried here twice, and a lookup that still fails is
 * reported as unsettled ({@link isSettled}) so the card asks again later.
 */
@Injectable({ providedIn: 'root' })
export class ProductCardsService {
  /** The server caps a reply at 50 cards, so one request normally covers a whole strip. */
  private static readonly MAX_SKUS_PER_REQUEST = 50;
  /** Rows per product page; joined duplicates mean a page can hold fewer products than this. */
  private static readonly PAGE_SIZE = 50;
  /** Pages a batch lookup will follow before settling on what it has found. */
  private static readonly MAX_PAGES = 10;
  /** Back-off before each in-request retry of a transient failure. */
  private static readonly RETRY_DELAYS_MS = [1_000, 3_000];

  /** SKU → card, or null when the product API did not return it (logged once, not retried). */
  private readonly resolved = new Map<string, ProductCard | null>();
  /** SKU → the request already fetching it, so a prefetch and a render share one call. */
  private readonly inFlight = new Map<string, Observable<void>>();
  /** Product link → card (null when nothing matched), for replies that carry no SKUs. */
  private readonly resolvedLinks = new Map<string, ProductCard | null>();
  /** Product link → the lookup already running for it, shared by every entry naming it. */
  private readonly linksInFlight = new Map<string, Observable<ProductCard | null>>();

  constructor(private baseService: BaseService) {}

  /** Start resolving early (on the frame, before any text); the strip shares the call. */
  prefetch(skus: string[]): void {
    this.resolve(skus).subscribe();
  }

  /** Cards already resolved for these SKUs, synchronously (for a re-created strip). */
  cached(skus: string[]): ProductCard[] | null {
    if (!skus.every((sku) => this.resolved.has(sku))) return null;
    return this.collect(skus);
  }

  /**
   * One slot per SKU, aligned with the list — null where the SKU did not resolve — so the
   * Nth reply entry can take the Nth card. Every entry asks with the reply's whole list, so
   * they share one lookup.
   */
  resolveEach(skus: string[]): Observable<(ProductCard | null)[]> {
    return this.resolve(skus).pipe(map(() => this.align(skus)));
  }

  /**
   * True once this SKU's lookup has an answer — a card, or "the product API did not return
   * it". False while it is pending or after its request failed, which is worth asking again.
   */
  isSettled(sku: string): boolean {
    return this.resolved.has(sku);
  }

  /** {@link resolveEach}, synchronously, when every SKU has already been looked up. */
  cachedEach(skus: string[]): (ProductCard | null)[] | null {
    return skus.every((sku) => this.resolved.has(sku)) ? this.align(skus) : null;
  }

  /**
   * The cards for these SKUs, in the SKUs' order. A SKU that does not resolve is
   * dropped and logged — the backend only sends live products, so it is a lookup
   * failure, never a reason to fail the whole strip.
   */
  resolve(skus: string[]): Observable<ProductCard[]> {
    const pending = [...new Set(skus.filter((sku) => !this.resolved.has(sku)))];
    this.chunk(pending.filter((sku) => !this.inFlight.has(sku))).forEach((batch) => this.fetch(batch));

    const waits = [
      ...new Set(pending.map((sku) => this.inFlight.get(sku)).filter((r): r is Observable<void> => !!r)),
    ];
    if (!waits.length) return of(this.collect(skus));
    return forkJoin(waits).pipe(map(() => this.collect(skus)));
  }

  /**
   * The card for a product the reply names by its storefront link, for a reply that came
   * with no `product_cards` frame (the internal agent links every product name to its
   * stored `https://www.gatherco.com.au/<slug>` page but sends no SKUs).
   *
   * Looks the product up by that slug exactly; a link built from another category's slug
   * misses there, so it falls back to the product family — the leading FULL-CAPS words of
   * the name, which the agent always keeps as stored (`AMARON Limestone – Aged` → AMARON) —
   * preferring the family member whose finish the name mentions. Cached per link for the
   * life of the app; a failed request is not cached.
   */
  resolveLink(href: string, name = ''): Observable<ProductCard | null> {
    if (this.resolvedLinks.has(href)) return of(this.resolvedLinks.get(href) ?? null);
    const running = this.linksInFlight.get(href);
    if (running) return running;

    const slug = this.slugOf(href);
    const family = this.familyOf(name);
    let failed = false;
    const bySlug = slug ? this.lookup('url_slug', slug, 1) : of([]);
    const request = bySlug.pipe(
      switchMap((products) =>
        products.length || !family
          ? of(products[0] ?? null)
          : this.lookup('family_name', family, 50).pipe(map((list) => this.pickByFinish(list, name))),
      ),
      map((product) => (product ? this.toCard(product) : null)),
      catchError((error: unknown) => {
        failed = true;
        console.warn('[Pebbl] product picture could not be loaded', error);
        return of(null);
      }),
      tap((card) => {
        if (!card && !failed) console.warn('[Pebbl] product link did not resolve', href);
        if (!failed) this.resolvedLinks.set(href, card);
      }),
      finalize(() => this.linksInFlight.delete(href)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.linksInFlight.set(href, request);
    return request;
  }

  /** {@link resolveLink}, synchronously, once that link has been looked up. */
  cachedLink(href: string): ProductCard | null | undefined {
    return this.resolvedLinks.has(href) ? this.resolvedLinks.get(href) ?? null : undefined;
  }

  /** Products whose `field` equals `value` exactly (v3 `filter[field]=value`). */
  private lookup(field: string, value: string, pageSize: number): Observable<any[]> {
    const params = new RequestParamModel();
    params.fields = ['id', 'sku', 'type', 'family_name', 'finish_edge_name', 'title'];
    params.expand = ['formatHeroImage'];
    // 'number' is the helper's exact-match branch (anything else is a LIKE); it does not
    // encode, and slugs carry `/`.
    params.addDefaultFilter(field, encodeURIComponent(value), 'number');
    params.pageSize = pageSize;
    return this.baseService
      .get('product', params, true, true)
      .pipe(this.retryTransient(), map((response: any) => (response?.body?.data ?? []) as any[]));
  }

  /** The storefront path without its slashes, query or hash — the product's `url_slug`. */
  private slugOf(href: string): string {
    try {
      return decodeURIComponent(new URL(href).pathname).replace(/^\/+|\/+$/g, '');
    } catch {
      return '';
    }
  }

  /** The leading FULL-CAPS words of a product name (`LE MANA Limestone` → LE MANA). */
  private familyOf(name: string): string {
    const words: string[] = [];
    for (const word of name.trim().split(/\s+/)) {
      if (!/[A-Z]/.test(word) || word !== word.toUpperCase()) break;
      words.push(word);
    }
    return words.join(' ');
  }

  /** The family member whose finish the name mentions, else the first. */
  private pickByFinish(products: any[], name: string): any | null {
    const tokens = (text: string): string[] => text.toLowerCase().match(/[a-z]+/g) ?? [];
    const wanted = new Set(tokens(name));
    let best: any | null = null;
    let bestScore = -1;
    for (const product of products) {
      const score = tokens(String(product?.finish_edge_name ?? '')).filter((t) => wanted.has(t)).length;
      if (score > bestScore) {
        best = product;
        bestScore = score;
      }
    }
    return best;
  }

  /** One lookup for a batch of SKUs, registered so concurrent callers share it. */
  private fetch(batch: string[]): void {
    // skipLoader + skipError: a background lookup must not flash the global spinner
    // or raise an error toast in the middle of a chat.
    const request = this.fetchProducts(batch).pipe(
      map((bySku) => {
        for (const sku of batch) {
          const product = bySku.get(sku);
          const card = product ? this.toCard(product) : null;
          if (!card) console.warn('[Pebbl] product card did not resolve', sku);
          this.resolved.set(sku, card);
        }
      }),
      catchError((error: unknown) => {
        console.warn('[Pebbl] product cards could not be loaded', error);
        return of(undefined);
      }),
      finalize(() => batch.forEach((sku) => this.inFlight.delete(sku))),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    batch.forEach((sku) => this.inFlight.set(sku, request));
  }

  /**
   * The products for a batch of SKUs, by SKU, following the API's pages until every SKU is
   * found or the pages run out.
   *
   * One page is NOT enough even with a page as large as the batch: v3 joins
   * `join_product_type_collection_category`, which holds a row per category the product is
   * in, and paginates those joined rows before collapsing them to products. Two SKUs in two
   * categories each came back as `X-Pagination-Total-Count: 4` with only ONE product on
   * page 1 of 2 — the other product's card silently had no picture and no "View product".
   */
  private fetchProducts(batch: string[], page = 1, found = new Map<string, any>()): Observable<Map<string, any>> {
    const params = new RequestParamModel();
    params.fields = ['id', 'sku', 'type', 'family_name', 'finish_edge_name', 'title'];
    params.expand = ['formatHeroImage'];
    // v3 reads the SINGULAR `filter[sku][in][i]` — `filterInWithArray` writes api-v2's
    // `filters[…]`, which v3 silently ignores and returns the first page of every product.
    // Percent-encodes each value — SKUs carry `/`, `+`, brackets and spaces.
    params.addDefaultFilterInWithArray(batch, 'sku');
    params.pageSize = ProductCardsService.PAGE_SIZE;
    params.pageIndex = page; // sent as Yii's 1-based `page`; page 1 is the default either way

    return this.baseService.get('product', params, true, true).pipe(
      this.retryTransient(),
      switchMap((response: any) => {
        for (const product of response?.body?.data ?? []) {
          if (typeof product?.sku === 'string' && !found.has(product.sku)) found.set(product.sku, product);
        }
        const pageCount = Number(response?.headers?.get?.('X-Pagination-Page-Count')) || 1;
        const lastPage = Math.min(pageCount, ProductCardsService.MAX_PAGES);
        return batch.every((sku) => found.has(sku)) || page >= lastPage
          ? of(found)
          : this.fetchProducts(batch, page + 1, found);
      }),
    );
  }

  /**
   * Retry a failure that may pass on its own — no response (network drop, timeout), 408,
   * 429 or a 5xx — after {@link RETRY_DELAYS_MS}. Any other 4xx is an answer and fails at once.
   */
  private retryTransient<T>(): MonoTypeOperatorFunction<T> {
    const delays = ProductCardsService.RETRY_DELAYS_MS;
    return retry<T>({
      count: delays.length,
      delay: (error: unknown, attempt: number) => {
        const status = error instanceof HttpErrorResponse ? error.status : 0;
        const transient = status === 0 || status === 408 || status === 429 || status >= 500;
        return transient ? timer(delays[attempt - 1]) : throwError(() => error);
      },
    });
  }

  /** Cards in the frame's order; unresolved SKUs dropped, duplicates kept. */
  private collect(skus: string[]): ProductCard[] {
    return this.align(skus).filter((card): card is ProductCard => !!card);
  }

  /** One slot per SKU, null where it did not resolve. */
  private align(skus: string[]): (ProductCard | null)[] {
    return skus.map((sku) => this.resolved.get(sku) ?? null);
  }

  private toCard(product: any): ProductCard | null {
    if (product.id == null) return null;
    const family = String(product.family_name ?? '').trim();
    const finish = String(product.finish_edge_name ?? '').trim();
    const name = family ? (finish ? `${family} - ${finish}` : family) : String(product.title ?? '').trim();
    if (!name) return null;
    const heroPath = product.formatHeroImage?.path;
    return {
      name,
      link: product.type ? ['/products', product.id, product.type] : ['/products', product.id],
      imageUrl: heroPath
        ? `${Constants.S3_PRODUCT_IMAGES_IMAGE_KIT_URL}/${heroPath}?tr=w-320,h-320,q-90,f-webp`
        : Constants.NO_IMAGE_PRODUCT,
    };
  }

  private chunk(skus: string[]): string[][] {
    const batches: string[][] = [];
    for (let i = 0; i < skus.length; i += ProductCardsService.MAX_SKUS_PER_REQUEST) {
      batches.push(skus.slice(i, i + ProductCardsService.MAX_SKUS_PER_REQUEST));
    }
    return batches;
  }
}
